import { addDays, format, set, startOfDay } from "date-fns";
import { ClientScheduleSlot } from "@/types/staffing/serviceRequest";
import { AvailabilitySlotPayload } from "@/services/staffing/Availability";
import { SlotSelectionPayload } from "@/services/staffing/Staffing";

export const TIME_STEP_MINUTES = 15;
/** Sentinel "end of day" value; rendered as midnight, saved as 00:00 of the next day / minute 1440. */
export const MIDNIGHT_END = "24:00";

const SLOT_DATE_FORMAT = "yyyy-MM-dd HH:mm:ss";
/** Fill colour / title the Availability app has always stored on user-defined blocks. */
const SLOT_COLOR = "#cdedba";
const SLOT_TITLE = "click to delete";

export interface TimeOption {
  value: string;
  label: string;
}

export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

export function formatTimeLabel(hhmm: string): string {
  if (hhmm === MIDNIGHT_END) return "12:00 AM (midnight)";
  const minutes = timeToMinutes(hhmm);
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

/** 00:00 … 23:45 in 15-minute steps, plus a midnight end-of-day option. */
export function generateTimeOptions(step = TIME_STEP_MINUTES): TimeOption[] {
  const options: TimeOption[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    const value = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
    options.push({ value, label: formatTimeLabel(value) });
  }
  options.push({ value: MIDNIGHT_END, label: formatTimeLabel(MIDNIGHT_END) });
  return options;
}

export function newScheduleSlot(day: number): ClientScheduleSlot {
  return {
    id: `slot-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    day,
    start: "",
    end: "",
  };
}

export function isSlotComplete(slot: ClientScheduleSlot): boolean {
  return slot.start !== "" && slot.end !== "";
}

export function isSlotRangeValid(slot: ClientScheduleSlot): boolean {
  return isSlotComplete(slot) && timeToMinutes(slot.end) > timeToMinutes(slot.start);
}

export interface ClientScheduleValidationOptions {
  /** The scheduling slot-selection endpoint accepts one range per day. */
  singleRangePerDay?: boolean;
}

/** Validation message for the Client Schedule field, or undefined when valid. */
export function getClientScheduleError(
  slots: ClientScheduleSlot[],
  { singleRangePerDay = false }: ClientScheduleValidationOptions = {},
): string | undefined {
  if (slots.length === 0)
    return "Client schedule is required — select at least one day and enter a time range";
  if (slots.some((s) => !isSlotComplete(s)))
    return "Enter a start and end time for each selected day";
  if (slots.some((s) => !isSlotRangeValid(s)))
    return "End time must be after start time";
  if (singleRangePerDay) {
    const days = slots.map((s) => s.day);
    if (new Set(days).size !== days.length)
      return "Only one time range per day can be submitted";
  }
  return undefined;
}

export function getScheduleTotalHours(slots: ClientScheduleSlot[]): number {
  return slots
    .filter(isSlotRangeValid)
    .reduce((total, s) => total + (timeToMinutes(s.end) - timeToMinutes(s.start)) / 60, 0);
}

// ---------------------------------------------------------------------------
// Client availability (PUT clientavailability) — "I need help finding staff"
// ---------------------------------------------------------------------------

/** The next occurrence (today or later) of a weekday, 0 = Sunday. */
function nextOccurrenceOfWeekday(day: number, now: Date): Date {
  let daysToAdd = day - now.getDay();
  if (daysToAdd < 0) daysToAdd += 7;
  return startOfDay(addDays(now, daysToAdd));
}

function atTime(base: Date, hhmm: string): Date {
  const minutes = timeToMinutes(hhmm);
  return set(addDays(base, Math.floor(minutes / (24 * 60))), {
    hours: Math.floor(minutes / 60) % 24,
    minutes: minutes % 60,
    seconds: 0,
    milliseconds: 0,
  });
}

/**
 * Converts the form slots into the weekly-recurring slot array the Availability
 * app stores. Incomplete or inverted ranges are dropped.
 */
export function buildAvailabilitySlots(
  slots: ClientScheduleSlot[],
  now: Date = new Date(),
): AvailabilitySlotPayload[] {
  return slots
    .filter(isSlotRangeValid)
    .sort((a, b) => a.day - b.day || timeToMinutes(a.start) - timeToMinutes(b.start))
    .map((slot) => {
      const base = nextOccurrenceOfWeekday(slot.day, now);
      return {
        start: format(atTime(base, slot.start), SLOT_DATE_FORMAT),
        end: format(atTime(base, slot.end), SLOT_DATE_FORMAT),
        recurring: "FREQ=WEEKLY",
        title: SLOT_TITLE,
        color: SLOT_COLOR,
        availabilityType: "User Defined",
      };
    });
}

/** JSON string for ClientAvailabilityRequest.data */
export function buildClientAvailabilityData(
  slots: ClientScheduleSlot[],
  now: Date = new Date(),
): string {
  return JSON.stringify(buildAvailabilitySlots(slots, now));
}

// ---------------------------------------------------------------------------
// Scheduling slot selection (POST staffing/scheduling/slots/selection) — Add Provider
// ---------------------------------------------------------------------------

/** {processId}-{staffLogin}-{clientId}, the reference the slot selection endpoint parses. */
export function buildSlotSelectionReferenceId(
  processId: number,
  staffLoginName: string,
  clientId: number,
): string {
  return `${processId}-${staffLoginName}-${clientId}`;
}

/**
 * Builds the slot card submit body: one `wBday{day}` key per selected day carrying a
 * weekly (`WB`) token in minutes from midnight. Incomplete or inverted ranges are dropped;
 * when a day somehow carries several ranges, the earliest is sent (the endpoint accepts
 * one per day).
 */
export function buildSlotSelectionPayload(
  slots: ClientScheduleSlot[],
  referenceId: string,
  triggeredBy: string,
): SlotSelectionPayload {
  const payload: SlotSelectionPayload = { referenceId, triggeredBy };
  const byDay = new Map<number, ClientScheduleSlot>();
  slots
    .filter(isSlotRangeValid)
    .sort((a, b) => a.day - b.day || timeToMinutes(a.start) - timeToMinutes(b.start))
    .forEach((slot) => {
      if (!byDay.has(slot.day)) byDay.set(slot.day, slot);
    });
  byDay.forEach((slot, day) => {
    payload[`wBday${day}`] = `WB|D${day}|${timeToMinutes(slot.start)}|${timeToMinutes(slot.end)}`;
  });
  return payload;
}
